import { createFileRoute, useParams, useLocation } from '@tanstack/react-router';
import { useTheme, useMediaQuery } from '@mui/material';
import { WeeklyGrainDashboardAnalysis, useAuth } from '@amber-ui/core';

/**
 * Dashboard analysis screenshot route — used by headless Chromium (Lambda) to
 * capture weekly grain bin dashboard analysis images for emails.
 *
 * Uses useAuth() instead of useGetViewer() to avoid the auth timing issue where
 * useQuery(GetViewerDocument) fires before the RenewToken mutation's cache write
 * completes. The AuthProvider context is guaranteed to be populated by the time
 * child route components render (the _auth layout's beforeLoad passes through on
 * first resolution, then re-evaluates once AuthProvider signals ready=true).
 *
 * The component only uses viewer.user_id for a security check — the auth context
 * provides this directly.
 */
function DashboardAnalysisRoute() {
  const { user_id, account_id, grain_bin_id } = useParams({
    from: '/_auth/_grain-bin/grain_bin/dashboard_analysis/$user_id/$account_id/$grain_bin_id',
  });
  const { pathname } = useLocation();
  const theme = useTheme();
  const mobile_width = useMediaQuery(theme.breakpoints.down('sm'));
  const { user_id: auth_user_id, ready } = useAuth();

  if (!ready || !auth_user_id) return null;

  // Construct a minimal viewer object — the component only accesses viewer.user_id
  // for a security check comparing the authenticated user against the URL param.
  const viewer = { user_id: auth_user_id };

  return (
    <WeeklyGrainDashboardAnalysis
      grain_bin_id={grain_bin_id}
      account_id={account_id}
      mobile_width={mobile_width}
      pathname={pathname}
      viewer={viewer}
      url_base="/grain_bin"
      routeParams={{ user_id, account_id, grain_bin_id }}
      hideExpCharts={true}
    />
  );
}

export const Route = createFileRoute(
  '/_auth/_grain-bin/grain_bin/dashboard_analysis/$user_id/$account_id/$grain_bin_id'
)({
  component: DashboardAnalysisRoute,
});
